import { parseLegacyPublicScopeTargets, resolvePublicScopeTargets } from '../../../lib/weeklyReportScopeTargets';

export type WeeklyReportPublicScopePayload = {
  scope_type: 'public' | 'teams' | 'department' | 'departments';
  department_ids: number[];
  team_ids: number[];
  private_flg: number;
  public_department: string | null;
  public_team: string | null;
};

type NormalizeContext = {
  departmentId: number;
  teamId: number;
  divisionOptions?: any[];
  teamOptions?: any[];
};

const readId = (option: any, keys: string[]): number | null => {
  for (const key of keys) {
    const value = Number(option?.[key]);
    if (Number.isInteger(value) && value > 0) return value;
  }
  return null;
};

const optionCode = (option: any, fallback: number): string =>
  String(option?.value ?? option?.code ?? option?.label ?? fallback).trim();

/**
 * 公開範囲の旧形式(public_team/public_department文字列)を、保存APIの正本であるID配列へ変換する。
 * 保存済みIDを優先し、解決不能な限定公開は保存を止める。
 */
export const normalizeWeeklyReportPublicScopeForSave = (
  pub: any,
  context: NormalizeContext,
): WeeklyReportPublicScopePayload => {
  const departmentId = Number(context.departmentId);
  const divisions = Array.isArray(context.divisionOptions) ? context.divisionOptions : [];
  const teams = Array.isArray(context.teamOptions) ? context.teamOptions : [];
  const publicTeamTokens = parseLegacyPublicScopeTargets(pub?.public_team);

  const divisionEntries = divisions
    .map((option) => {
      const id = readId(option, ['department_id', 'id']);
      return id == null ? null : { id, option };
    })
    .filter((entry): entry is { id: number; option: any } => entry !== null);

  const teamEntries = teams
    .map((option) => {
      const id = readId(option, ['team_id', 'id']);
      const optionDepartmentId = readId(option, ['department_id', 'departmentId']);
      return id == null ? null : { id, optionDepartmentId, option };
    })
    .filter((entry): entry is { id: number; optionDepartmentId: number | null; option: any } => entry !== null);

  const hasTeamDepartmentData = teamEntries.some((entry) => entry.optionDepartmentId != null);
  const availableTeamEntries = hasTeamDepartmentData && Number.isInteger(departmentId) && departmentId > 0
    ? teamEntries.filter((entry) => entry.optionDepartmentId === departmentId)
    : teamEntries;

  const targetOptions = (entries: { id: number; option: any }[]) => entries.map(({ id, option }) => ({
    id, code: option.value ?? option.code, label: option.label ?? option.name,
  }));
  const requestedTeams = resolvePublicScopeTargets(pub?.team_ids, pub?.public_team, targetOptions(availableTeamEntries));
  const requestedDepartments = resolvePublicScopeTargets(pub?.department_ids, pub?.public_department, targetOptions(divisionEntries));
  const explicitScope = String(pub?.scope_type ?? '').trim().toLowerCase();
  const privateFlag = Number(pub?.private_flg);

  const hasLegacyTeamTarget = requestedTeams.ids.length > 0 || publicTeamTokens.length > 0;
  const scopeType = (
    ['public', 'teams', 'department', 'departments'].includes(explicitScope)
      ? explicitScope
      : privateFlag === 2
        ? 'departments'
        : privateFlag === 1
          ? (hasLegacyTeamTarget ? 'teams' : 'department')
          : 'public'
  ) as WeeklyReportPublicScopePayload['scope_type'];

  let departmentIds: number[] = [];
  let teamIds: number[] = [];
  if (scopeType === 'department') {
    if (!Number.isSafeInteger(departmentId) || departmentId <= 0) {
      throw new Error('課情報を取得できません。課を確認してください。');
    }
    departmentIds = [departmentId];
  } else if (scopeType === 'departments') {
    departmentIds = requestedDepartments.ids.map(Number);
    if (departmentIds.length === 0 || requestedDepartments.unresolved.length > 0) {
      throw new Error('公開先の課を復元できません。公開範囲設定で課を選び直してください。');
    }
  } else if (scopeType === 'teams') {
    teamIds = requestedTeams.ids.map(Number);
    if (teamIds.length === 0 || requestedTeams.unresolved.length > 0) {
      throw new Error('公開先のチームを復元できません。公開範囲設定でチームを選び直してください。');
    }
    if (teamEntries.some((entry) => teamIds.includes(entry.id) && entry.optionDepartmentId != null && entry.optionDepartmentId !== departmentId)) {
      throw new Error('別の課のチームが公開先に含まれています。公開範囲設定を確認してください。');
    }
  }

  const resolvedDepartmentCodes = departmentIds
    .map((id) => {
      const entry = divisionEntries.find((candidate) => candidate.id === id);
      return entry ? optionCode(entry.option, id) : String(id);
    })
    .filter(Boolean);
  const resolvedTeamCodes = teamIds
    .map((id) => {
      const entry = availableTeamEntries.find((candidate) => candidate.id === id);
      return entry ? optionCode(entry.option, id) : String(id);
    })
    .filter(Boolean);

  return {
    scope_type: scopeType,
    department_ids: departmentIds,
    team_ids: teamIds,
    private_flg: scopeType === 'public' ? 0 : (scopeType === 'departments' ? 2 : 1),
    public_department: scopeType === 'department' || scopeType === 'departments'
      ? (resolvedDepartmentCodes.join(',') || null)
      : null,
    public_team: scopeType === 'teams' ? (resolvedTeamCodes.join(',') || null) : null,
  };
};

/** UI-only marker. Never send it to the API. */
export const PUBLIC_SCOPE_EDITED = '__ui_public_scope_edited';

/**
 * A classification update submits all option rows, but public scopes are patches.
 * Omit untouched scopes so a broken legacy target in another row neither blocks
 * this edit nor overwrites that row. The existing API preserves omitted scopes.
 */
export const prepareWeeklyReportPublicScopeOption = (
  option: Record<string, any>,
  context: NormalizeContext,
  isWeeklyReportClassification: boolean,
): Record<string, any> => {
  const next = { ...option };
  const explicitlyEdited = next[PUBLIC_SCOPE_EDITED] === true;
  const isNewOption = next.__ui_new === true;
  delete next[PUBLIC_SCOPE_EDITED];
  const hasPublicSetting = next.public && (
    next.public.scope_type != null || next.public.private_flg != null
  );

  if (isWeeklyReportClassification && !explicitlyEdited && !isNewOption) {
    delete next.public;
    return next;
  }
  if (isWeeklyReportClassification && !hasPublicSetting) {
    if (explicitlyEdited) {
      throw new Error('公開範囲設定が不正です。公開先を確認してください。');
    }
    delete next.public;
    return next;
  }
  try {
    next.public = normalizeWeeklyReportPublicScopeForSave(next.public, context);
  } catch (error) {
    const reason = error instanceof Error ? error.message : '公開範囲を確認してください。';
    throw new Error(`${next.label ?? next.value ?? '週報分類'}: ${reason}`);
  }
  return next;
};
